import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logoutAction } from 'features/common/commonSlice';
import { searchExpedients, searchPatientExpedients } from 'services/api/endpoints/gdp/expedients';
import { i18n } from 'utils';

/**
 * Actividad por expedientes: la vista general (clave `dashboard`) y la pestaña de actividad de
 * cada ficha (clave = contextKey de la ficha) comparten slice, cada una con su resultado, sus
 * facetas y su selector de vista Eventos | Expedientes.
 */
export type ActivityView = 'events' | 'expedients';

export const DASHBOARD_KEY = 'dashboard';

export interface ExpedientsSearchScope {
  items: any[];
  total: number;
  filterableValues: any;
  status: 'idle' | 'loading' | 'failed';
}

export interface ExpedientsSearchState {
  byKey: Record<string, ExpedientsSearchScope>;
  viewByKey: Record<string, ActivityView>;
}

export const emptyScope = (): ExpedientsSearchScope => ({ items: [], total: 0, filterableValues: undefined, status: 'idle' });

const initialState: ExpedientsSearchState = { byKey: {}, viewByKey: {} };

/** Fila de la tabla a partir de la respuesta de gdp (ExpedientSearchRow). */
export const normalizeExpedientsTable = (payload: any): any[] => {
  const items = payload?.item?.items ?? [];
  return items.map((item: any) => ({
    id: item.id,
    expedientId: item.id,
    patientId: item.patientId,
    date: item.openTmst,
    closeDate: item.closeTmst,
    name: item.patientName ?? '',
    user: item.patientName ?? '',
    userAccess: item.patientAccess,
    patientServiceNumber: item.patientServiceNumber,
    group: item.groupName,
    groupId: item.groupId,
    eventCount: item.eventCount,
    eventTypes: (item.eventTypes ?? []).map((type: string) => i18n(`measure.card.type.${type}`)),
    rawEventTypes: item.eventTypes ?? [],
    // «En espera» (solo gdp) tiene fecha de cierre del SAC pero no está cerrado.
    state: i18n(item.waiting ? 'expedient.dialog.state.waiting' : item.closeTmst ? 'expedient.dialog.state.closed' : 'expedient.dialog.state.open'),
    rawState: item.waiting ? 'WAITING' : item.closeTmst ? 'CLOSED' : 'OPEN',
    resolutionCode: item.resolutionCode ?? null,
    resolved: !!item.resolutionCode,
    resolutionTimestamp: item.resolutionTmst,
    applicantName: item.resolutionApplicantName ?? '',
    hasNotes: !!item.hasNotes,
    informative: !!item.informative,
    firstEventTmst: item.firstEventTmst,
    lastEventTmst: item.lastEventTmst,
  }));
};

// `page` lo añade TableComponent y `contextKey` es de la tabla: no viajan al backend.
export const searchExpedientsAsync: any = createAsyncThunk('expedientsSearch/search', async (data: any) => {
  const { page, contextKey, ...body } = data ?? {};
  const response = await searchExpedients(body);
  return response?.data;
});

export const searchPatientExpedientsAsync: any = createAsyncThunk('expedientsSearch/searchPatient', async (data: any) => {
  const { page, contextKey, id, ...body } = data ?? {};
  const response = await searchPatientExpedients({ id, ...body });
  return response?.data;
});

const scopeOf = (state: ExpedientsSearchState, key: string): ExpedientsSearchScope => {
  if (!state.byKey[key]) state.byKey[key] = emptyScope();
  return state.byKey[key];
};

const patientKey = (arg: any): string => String(arg?.contextKey ?? arg?.id ?? 'main');

const applyResult = (scope: ExpedientsSearchScope, payload: any, init: any) => {
  scope.status = 'idle';
  scope.items = normalizeExpedientsTable(payload);
  // total + facetas sólo vienen en la primera página (init==0); en paginación se conservan.
  if (!init) {
    scope.filterableValues = payload?.item?.filterableValues;
    scope.total = payload?.item?.total ?? 0;
  }
};

export const expedientsSearchSlice = createSlice({
  name: 'expedientsSearch',
  initialState,
  reducers: {
    setActivityView: (state, action: PayloadAction<{ key: string; view: ActivityView }>) => {
      state.viewByKey[action.payload.key] = action.payload.view;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutAction, () => initialState)
      .addCase(searchExpedientsAsync.pending, (state) => {
        scopeOf(state, DASHBOARD_KEY).status = 'loading';
      })
      .addCase(searchExpedientsAsync.fulfilled, (state, action: any) => {
        applyResult(scopeOf(state, DASHBOARD_KEY), action.payload, action.meta?.arg?.init);
      })
      .addCase(searchExpedientsAsync.rejected, (state) => {
        scopeOf(state, DASHBOARD_KEY).status = 'failed';
      })
      .addCase(searchPatientExpedientsAsync.pending, (state, action: any) => {
        scopeOf(state, patientKey(action.meta?.arg)).status = 'loading';
      })
      .addCase(searchPatientExpedientsAsync.fulfilled, (state, action: any) => {
        applyResult(scopeOf(state, patientKey(action.meta?.arg)), action.payload, action.meta?.arg?.init);
      })
      .addCase(searchPatientExpedientsAsync.rejected, (state, action: any) => {
        scopeOf(state, patientKey(action.meta?.arg)).status = 'failed';
      });
  },
});

export const { setActivityView } = expedientsSearchSlice.actions;

export default expedientsSearchSlice.reducer;
